// MANAGER APPROVAL SCREEN
// Allows managers to review, approve, and reject team timesheet submissions.
// Phase 6: Manager Approval & Reporting

import React, { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Modal, Form, Alert, Badge, Table } from 'react-bootstrap'
import { format, startOfWeek, endOfWeek, subWeeks, addWeeks } from 'date-fns'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import { useAuth } from '../../modules/auth'
import {
  getPendingApprovals,
  approveTimesheetEntry,
  rejectTimesheetEntry,
  bulkApproveEntries,
  getProjectCodesDropdown,
  TimesheetEntry,
} from '../../../api/TimesheetAPI'
import { getTeamMembers } from '../../../api/AdminAPI'
//Export Button Added
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"

interface ApprovalSummary {
  pendingCount: number
  approvedCount: number
  rejectedCount: number
  teamSize: number
}

interface ApprovalsData {
  entries: TimesheetEntry[]
  summary: ApprovalSummary
}

const ManagerApproval = () => {
  const { currentUser } = useAuth()
  const managerId = currentUser?.id || ''

  // Date range state (default: current week)
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  )

  // Data states
  const [approvalsData, setApprovalsData] = useState<ApprovalsData | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Filter states
  const [activeTab, setActiveTab] = useState<string>('SUBMITTED') // SUBMITTED, APPROVED, REJECTED, ALL
  const [selectedEmployee, setSelectedEmployee] = useState<string>('')
  const [selectedProject, setSelectedProject] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState<string>('')

  // Dropdown options
  const [projectCodes, setProjectCodes] = useState<any[]>([])
  const [teamMembers, setTeamMembers] = useState<any[]>([])

  // Selection states
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set())
  const [selectAll, setSelectAll] = useState(false)

  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<TimesheetEntry | null>(null)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [managerRemarks, setManagerRemarks] = useState('')
  const [showTeamModal, setShowTeamModal] = useState(false)

  // Load data on mount and when filters change
  useEffect(() => {
    if (!managerId) return
    loadApprovals()
    loadProjectCodes()
  }, [managerId, currentWeekStart, activeTab, selectedEmployee, selectedProject])

  // Load team members after approvals data is loaded (for fallback)
  useEffect(() => {
    if (!managerId) return
    loadTeamMembers()
  }, [managerId, approvalsData])

  const loadTeamMembers = async () => {
    try {
      // Fetch team members from User API (based on UserAssociate relationship)
      const response = await getTeamMembers(managerId)


      if (response.data?.statusCode === 200 && Array.isArray(response.data.data)) {
        // Map to the format we need
        const members = response.data.data.map((user: any) => ({
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
          employeeCode: user.employeeCode,
        }))
        setTeamMembers(members)
      } else {
        console.warn('Team members API returned unexpected format:', response.data)
        // Fallback: Extract from approvals data if available
        if (approvalsData && approvalsData.entries.length > 0) {
          const uniqueEmployees = Array.from(
            new Map(
              approvalsData.entries.map((e: any) => [
                e.user?.id,
                {
                  id: e.user?.id,
                  name: `${e.user?.firstName} ${e.user?.lastName}`,
                  employeeCode: e.user?.employeeCode,
                },
              ])
            ).values()
          )
          setTeamMembers(uniqueEmployees as any[])
        }
      }
    } catch (error: any) {
      console.error('Error loading team members:', error)
      // Fallback: Extract from approvals data if available
      if (approvalsData && approvalsData.entries.length > 0) {
        const uniqueEmployees = Array.from(
          new Map(
            approvalsData.entries.map((e: any) => [
              e.user?.id,
              {
                id: e.user?.id,
                name: `${e.user?.firstName} ${e.user?.lastName}`,
                employeeCode: e.user?.employeeCode,
              },
            ])
          ).values()
        )
        setTeamMembers(uniqueEmployees as any[])
      }
    }
  }

  const loadApprovals = async () => {
    try {
      setLoading(true)
      const response = await getPendingApprovals({
        managerId,
        startDate: format(currentWeekStart, 'yyyy-MM-dd'),
        endDate: format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        userId: selectedEmployee || undefined,
        projectCodeId: selectedProject || undefined,
        status: activeTab === 'ALL' ? undefined : activeTab,
      })

      if (response.data?.statusCode === 200) {
        setApprovalsData(response.data.data)
      }
    } catch (error: any) {
      console.error('Error loading approvals:', error)
      toast.error(error.response?.data?.message || 'Failed to load approvals')
    } finally {
      setLoading(false)
    }
  }

  const loadProjectCodes = async () => {
    try {
      const response = await getProjectCodesDropdown()
      if (response.data?.statusCode === 200) {
        setProjectCodes(response.data.data)
      }
    } catch (error) {
      console.error('Error loading project codes:', error)
    }
  }
  // Filter entries by search term + NAME & employee code bases also
  const filteredEntries = approvalsData?.entries.filter((entry) => {
  if (!searchTerm) return true
  const search = searchTerm.toLowerCase()

  const fullName =
    `${entry.user?.firstName || ''} ${entry.user?.lastName || ''}`.toLowerCase()

  const employeeCode = entry.user?.employeeCode?.toLowerCase() || ''

  return (
    entry.description?.toLowerCase().includes(search) ||
    entry.projectCode?.name?.toLowerCase().includes(search) ||
    entry.projectCode?.code?.toLowerCase().includes(search) ||
    entry.tool?.name?.toLowerCase().includes(search) ||
    entry.taskType?.name?.toLowerCase().includes(search) ||
    fullName.includes(search) ||
    employeeCode.includes(search)
  )
})


  //Export Button added
  // Export Timesheet Data
  const handleExportTimesheet = () => {

    if (!filteredEntries || filteredEntries.length === 0) {
      toast.warning("No data available to export")
      return
    }

    // const exportData = filteredEntries.map((entry) => ({
    //   "Employee Name": `${entry.user?.firstName} ${entry.user?.lastName}`,
    //   "Employee Code": entry.user?.employeeCode,
    //   "Date": format(new Date(entry.date), "MMM dd, yyyy"),
    //   "Project": `${entry.projectCode?.code} - ${entry.projectCode?.name}`,
    //   "Tool": entry.tool?.name,
    //   "Task Type": entry.taskType?.name,
    //   "Hours": `${entry.billableHours}/${entry.actualHours}h`,
    //   "Status": entry.status
    // }))

    const exportData = filteredEntries.map((entry) => {

      //  declare pm
      const pm = (entry.projectCode as any)?.projectManager;
      const rmData = (entry as any)?.rmData || null; 

      return {
        "Employee Name": `${entry.user?.firstName || ''} ${entry.user?.lastName || ''}`,
        "Employee Code": entry.user?.employeeCode || "",
        "Date": format(new Date(entry.date), "MMM dd, yyyy"),
        "Department": entry.user?.department?.name || "N/A",
        "Project Code": entry.projectCode?.code || "",
        "Project Name": entry.projectCode?.name || "",
        "Project Manager": pm ? `${pm.firstName} ${pm.lastName}` : "N/A",
        "Reporting Manager": entry.reportingManager || "N/A",
        "RM Code": rmData?.employeeCode || "N/A",
        "Tool": entry.tool?.name || "",
        "Task Type": entry.taskType?.name,
        "Description": entry.description || "",
        "Billing Hours": entry.billableHours || 0,
        "Actual Hours": entry.actualHours || 0,
        "Overtime": entry.isOvertime ? "Yes" : "No",
        "Status": entry.status
      }
    })

    const worksheet = XLSX.utils.json_to_sheet(exportData)

    worksheet["!cols"] = [
      { wch: 25 }, // Employee Name
      { wch: 18 }, // Employee Code
      { wch: 15 }, // Date
      { wch: 20 }, // Department
      { wch: 18 }, // Project Code
      { wch: 25 }, // Project Name
      { wch: 25 }, // Project Manager
      { wch: 25 }, // Reporting Manager
      { wch: 18 }, // RM Code
      { wch: 15 }, // Tool
      { wch: 20 }, // Task Type
      { wch: 40 }, // Description
      { wch: 15 }, // Billing Hours
      { wch: 15 }, // Actual Hours
      { wch: 12 }, // Overtime
      { wch: 15 }, // Status
    ];

    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(workbook, worksheet, "Timesheet")

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    })

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream"
    })
    const weekStart = format(currentWeekStart, "MMMdd")
    const weekEnd = format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), "MMMdd")

    // saveAs(blob, "Timesheet_Approvals.xlsx")
    const fileName = `Timesheet_${activeTab}_${weekStart}-${weekEnd}.xlsx`
    saveAs(blob, fileName)
  }

  // Week navigation
  const goToPreviousWeek = () => setCurrentWeekStart((prev) => subWeeks(prev, 1))
  const goToNextWeek = () => setCurrentWeekStart((prev) => addWeeks(prev, 1))
  const goToCurrentWeek = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))

  // Selection handlers
  const handleSelectEntry = (entryId: string) => {
    const newSelected = new Set(selectedEntries)
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId)
    } else {
      newSelected.add(entryId)
    }
    setSelectedEntries(newSelected)
    setSelectAll(newSelected.size === filteredEntries?.length)
  }

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEntries(new Set())
      setSelectAll(false)
    } else {
      const allIds = new Set(filteredEntries?.map((e) => e.id) || [])
      setSelectedEntries(allIds)
      setSelectAll(true)
    }
  }

  // Open details modal
  const openDetailsModal = (entry: TimesheetEntry) => {
    setSelectedEntry(entry)
    setShowDetailsModal(true)
  }

  // Close details modal
  const closeDetailsModal = () => {
    setShowDetailsModal(false)
    setSelectedEntry(null)
    setRejectReason('')
    setManagerRemarks('')
  }

  // Approve single entry
  const handleApproveEntry = async (entry: TimesheetEntry) => {
    const result = await Swal.fire({
      title: 'Approve Entry?',
      html: `
        <p><strong>Employee:</strong> ${entry.user?.firstName} ${entry.user?.lastName}</p>
        <p><strong>Date:</strong> ${format(new Date(entry.date), 'MMM dd, yyyy')}</p>
        <p><strong>Hours:</strong> ${entry.billableHours}/${entry.actualHours}h</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Approve',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#28a745',
    })

    if (result.isConfirmed) {
      try {
        setSubmitting(true)
        const response = await approveTimesheetEntry({
          entryId: entry.id,
          approvedById: managerId,
        })

        if (response.data?.statusCode === 200) {
          toast.success('Entry approved successfully')
          loadApprovals()
          closeDetailsModal()
        }
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to approve entry')
      } finally {
        setSubmitting(false)
      }
    }
  }

  // Open reject modal
  const openRejectModal = (entry: TimesheetEntry) => {
    setSelectedEntry(entry)
    setShowRejectModal(true)
    setShowDetailsModal(false)
  }

  // Handle rejection
  const handleRejectEntry = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection')
      return
    }

    if (!selectedEntry) return

    try {
      setSubmitting(true)
      const response = await rejectTimesheetEntry({
        entryId: selectedEntry.id,
        approvedById: managerId,
        rejectedReason: rejectReason,
        managerRemarks: managerRemarks || undefined,
      })

      if (response.data?.statusCode === 200) {
        toast.success('Entry rejected successfully')
        loadApprovals()
        setShowRejectModal(false)
        setRejectReason('')
        setManagerRemarks('')
        setSelectedEntry(null)
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject entry')
    } finally {
      setSubmitting(false)
    }
  }

  // Bulk approve
  const handleBulkApprove = async () => {
    if (selectedEntries.size === 0) {
      toast.warning('Please select entries to approve')
      return
    }

    const result = await Swal.fire({
      title: 'Approve Multiple Entries?',
      html: `
        <p>You are about to approve <strong>${selectedEntries.size}</strong> timesheet entries.</p>
        <p>This action cannot be undone.</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Approve All (${selectedEntries.size})`,
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#28a745',
    })

    if (result.isConfirmed) {
      try {
        setSubmitting(true)
        const response = await bulkApproveEntries({
          entryIds: Array.from(selectedEntries),
          approvedById: managerId,
        })

        if (response.data?.statusCode === 200) {
          toast.success(`Successfully approved ${selectedEntries.size} entries`)
          setSelectedEntries(new Set())
          setSelectAll(false)
          loadApprovals()
        }
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to approve entries')
      } finally {
        setSubmitting(false)
      }
    }
  }

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUBMITTED':
        return 'warning'
      case 'APPROVED':
        return 'success'
      case 'REJECTED':
        return 'danger'
      case 'DRAFT':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  // Early return if user not loaded
  if (!currentUser || !managerId) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '400px' }}>
        <div className='text-center'>
          <div className='spinner-border text-primary mb-3' role='status' />
          <p className='text-muted'>Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Page Header */}
      <Row className='mb-4'>
        <Col>
          <h3 className='mb-0'>
            <i className='fas fa-clipboard-check me-2'></i>
            Timesheet Approvals
          </h3>
        </Col>
      </Row>

      {/* Summary Cards */}
      {approvalsData && (
        <Row className='mb-4'>
          <Col md={3}>
            <Card className='shadow-sm border-warning' style={{ cursor: 'pointer' }} onClick={() => setActiveTab('SUBMITTED')}>
              <Card.Body className='text-center'>
                <h2 className='mb-0 text-warning'>{approvalsData.summary.pendingCount}</h2>
                <small className='text-muted'>Pending</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className='shadow-sm border-success' style={{ cursor: 'pointer' }} onClick={() => setActiveTab('APPROVED')}>
              <Card.Body className='text-center'>
                <h2 className='mb-0 text-success'>{approvalsData.summary.approvedCount}</h2>
                <small className='text-muted'>Approved</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className='shadow-sm border-danger' style={{ cursor: 'pointer' }} onClick={() => setActiveTab('REJECTED')}>
              <Card.Body className='text-center'>
                <h2 className='mb-0 text-danger'>{approvalsData.summary.rejectedCount}</h2>
                <small className='text-muted'>Rejected</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card
              className='shadow-sm border-primary'
              style={{ cursor: 'pointer' }}
              onClick={() => setShowTeamModal(true)}
            >
              <Card.Body className='text-center'>
                <h2 className='mb-0 text-primary'>{approvalsData.summary.teamSize}</h2>
                <small className='text-muted'>👥 Team Size</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Week Navigation */}
      <Row className='mb-4'>
        <Col>
          <Card>
            <Card.Body className='d-flex justify-content-between align-items-center'>
              <Button variant='outline-secondary' onClick={goToPreviousWeek} size='sm'>
                <i className='fas fa-chevron-left me-2'></i>
                Previous Week
              </Button>
              <div className='text-center'>
                <strong>
                  {format(currentWeekStart, 'MMM dd')} -{' '}
                  {format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'MMM dd, yyyy')}
                </strong>
                <br />
                <Button size='sm' variant='link' onClick={goToCurrentWeek}>
                  Go to Current Week
                </Button>
              </div>
              <Button variant='outline-secondary' onClick={goToNextWeek} size='sm'>
                Next Week
                <i className='fas fa-chevron-right ms-2'></i>
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Row className='mb-3'>
        <Col>
          <ul className='nav nav-tabs'>
            <li className='nav-item'>
              <button
                className={`nav-link ${activeTab === 'ALL' ? 'active' : ''}`}
                onClick={() => setActiveTab('ALL')}
              >
                All
              </button>
            </li>
            <li className='nav-item'>
              <button
                className={`nav-link ${activeTab === 'SUBMITTED' ? 'active' : ''}`}
                onClick={() => setActiveTab('SUBMITTED')}
              >
                Pending
                {approvalsData && approvalsData.summary.pendingCount > 0 && (
                  <Badge bg='warning' className='ms-2'>
                    {approvalsData.summary.pendingCount}
                  </Badge>
                )}
              </button>
            </li>
            <li className='nav-item'>
              <button
                className={`nav-link ${activeTab === 'APPROVED' ? 'active' : ''}`}
                onClick={() => setActiveTab('APPROVED')}
              >
                Approved
              </button>
            </li>
            <li className='nav-item'>
              <button
                className={`nav-link ${activeTab === 'REJECTED' ? 'active' : ''}`}
                onClick={() => setActiveTab('REJECTED')}
              >
                Rejected
              </button>
            </li>
          </ul>
        </Col>
      </Row>

      {/* Filters & Actions */}
      <Row className='mb-3'>
        <Col md={3}>
          <Form.Select
            size='sm'
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            <option value=''>All Team Members</option>
            {teamMembers.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select
            size='sm'
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
          >
            <option value=''>All Projects</option>
            {projectCodes.map((pc) => (
              <option key={pc.id} value={pc.id}>
                {pc.code} - {pc.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Control
            size='sm'
            type='text'
            placeholder='Search...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md={3} className='text-end'>
        {/* Export Button Added */}
          <Button
            size="sm"
            variant="success"
            className="me-2"
            onClick={handleExportTimesheet}
          >
            <i className="fas fa-file-excel me-2"></i>
            Export to Excel
          </Button>
          {activeTab === 'SUBMITTED' && selectedEntries.size > 0 && (
            <Button size='sm' variant='success' onClick={handleBulkApprove} disabled={submitting}>
              <i className='fas fa-check me-1'></i>
              Approve Selected ({selectedEntries.size})
            </Button>
          )}
        </Col>
      </Row>

      {/* Table */}
      <Row>
        <Col>
          <Card>
            <Card.Body>
              {loading ? (
                <div className='text-center py-5'>
                  <div className='spinner-border text-primary' />
                  <p className='mt-2 text-muted'>Loading entries...</p>
                </div>
              ) : filteredEntries && filteredEntries.length > 0 ? (
                <Table responsive hover>
                  <thead>
                    <tr>
                      {activeTab === 'SUBMITTED' && (
                        <th style={{ width: '50px' }}>
                          <Form.Check
                            type='checkbox'
                            checked={selectAll}
                            onChange={handleSelectAll}
                          />
                        </th>
                      )}
                      <th>Employee</th>
                      <th>Date</th>
                      <th>Project</th>
                      <th>Tool</th>
                      <th>Task Type</th>
                      <th>Hours</th>
                      <th>Status</th>
                      <th>Reporting Manager</th>
                      <th>Reporting Manager Code</th>
                      <th style={{ width: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEntries.map((entry) => (
                      <tr
                        key={entry.id}
                        className={entry.isOvertime ? 'table-warning' : ''}
                      >
                        {activeTab === 'SUBMITTED' && (
                          <td>
                            <Form.Check
                              type='checkbox'
                              checked={selectedEntries.has(entry.id)}
                              onChange={() => handleSelectEntry(entry.id)}
                            />
                          </td>
                        )}
                        <td>
                          <strong>
                            {entry.user?.firstName} {entry.user?.lastName}
                          </strong>
                          <br />
                          <small className='text-muted'>{entry.user?.employeeCode}</small>
                        </td>
                        <td>{format(new Date(entry.date), 'MMM dd, yyyy')}</td>
                        <td>
                          <strong>{entry.projectCode?.code}</strong>
                          <br />
                          <small className='text-muted'>{entry.projectCode?.name}</small>
                        </td>
                        <td>{entry.tool?.name}</td>
                        <td>{entry.taskType?.name}</td>
                        
                        <td>
                          <strong>{entry.billableHours}/{entry.actualHours}h</strong>
                          {entry.isOvertime && (
                            <>
                              <br />
                              <Badge bg='warning' text='dark'>
                                OT
                              </Badge>
                            </>
                          )}
                        </td>
                        <td>
                          <Badge bg={getStatusColor(entry.status)}>{entry.status}</Badge>
                        </td>
                        <td>
                          {entry.reportingManager || "N/A"}
                        </td>
                        <td>
                          {(entry as any)?.rmData?.employeeCode || "N/A"}
                        </td>
                        <td>
                          <Button
                            size='sm'
                            variant='outline-primary'
                            onClick={() => openDetailsModal(entry)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant='light' className='text-center mb-0'>
                  <i className='fas fa-inbox fa-3x text-muted mb-3 d-block'></i>
                  <p className='mb-0'>No entries found for the selected filters</p>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={closeDetailsModal} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Timesheet Entry Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEntry && (
            <>
              <Row className='mb-3'>
                <Col md={6}>
                  <strong>Employee:</strong>
                  <br />
                  {selectedEntry.user?.firstName} {selectedEntry.user?.lastName} (
                  {selectedEntry.user?.employeeCode})
                </Col>
                <Col md={6}>
                  <strong>Date:</strong>
                  <br />
                  {format(new Date(selectedEntry.date), 'EEEE, MMM dd, yyyy')}
                </Col>
              </Row>

              <Row className='mb-3'>
                <Col md={12}>
                  <strong>Status:</strong>
                  <br />
                  <Badge bg={getStatusColor(selectedEntry.status)} className='mt-1'>
                    {selectedEntry.status}
                  </Badge>
                </Col>
              </Row>

              <hr />

              <Row className='mb-3'>
                <Col md={6}>
                  <strong>Project:</strong>
                  <br />
                  {selectedEntry.projectCode?.code} - {selectedEntry.projectCode?.name}
                </Col>
                <Col md={6}>
                  <strong>Tool:</strong>
                  <br />
                  {selectedEntry.tool?.name}
                </Col>
              </Row>

              <Row className='mb-3'>
                <Col md={6}>
                  <strong>Task Type:</strong>
                  <br />
                  {selectedEntry.taskType?.name}
                </Col>
                {selectedEntry.task && (
                  <Col md={6}>
                    <strong>Task:</strong>
                    <br />
                    {selectedEntry.task.title}
                  </Col>
                )}
              </Row>

              <Row className='mb-3'>
                <Col md={12}>
                  <strong>Description:</strong>
                  <div className='border rounded p-3 bg-light mt-2'>
                    {selectedEntry.description}
                  </div>
                </Col>
              </Row>

              <Row className='mb-3'>
                <Col md={4}>
                  <strong>Billable Hours:</strong>
                  <br />
                  {selectedEntry.billableHours}h
                </Col>
                <Col md={4}>
                  <strong>Actual Hours:</strong>
                  <br />
                  {selectedEntry.actualHours}h
                </Col>
                <Col md={4}>
                  <strong>Overtime:</strong>
                  <br />
                  {selectedEntry.isOvertime ? (
                    <Badge bg='warning' text='dark'>
                      Yes
                    </Badge>
                  ) : (
                    <Badge bg='secondary'>No</Badge>
                  )}
                </Col>
              </Row>

              {selectedEntry.status === 'REJECTED' && selectedEntry.rejectedReason && (
                <Alert variant='danger'>
                  <strong>Rejection Reason:</strong>
                  <br />
                  {selectedEntry.rejectedReason}
                </Alert>
              )}

              {selectedEntry.managerRemarks && (
                <Alert variant='info'>
                  <strong>Manager Remarks:</strong>
                  <br />
                  {selectedEntry.managerRemarks}
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={closeDetailsModal}>
            Close
          </Button>
          {selectedEntry?.status === 'SUBMITTED' && (
            <>
              <Button
                variant='danger'
                onClick={() => openRejectModal(selectedEntry)}
                disabled={submitting}
              >
                <i className='fas fa-times me-1'></i>
                Reject
              </Button>
              <Button
                variant='success'
                onClick={() => handleApproveEntry(selectedEntry)}
                disabled={submitting}
              >
                <i className='fas fa-check me-1'></i>
                Approve
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reject Timesheet Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEntry && (
            <>
              <p>
                <strong>Employee:</strong> {selectedEntry.user?.firstName}{' '}
                {selectedEntry.user?.lastName}
              </p>
              <p>
                <strong>Date:</strong> {format(new Date(selectedEntry.date), 'MMM dd, yyyy')}
              </p>
              <p>
                <strong>Hours:</strong> {selectedEntry.billableHours}/{selectedEntry.actualHours}h
              </p>

              <hr />

              <Form.Group className='mb-3'>
                <Form.Label>
                  Reason for Rejection <span className='text-danger'>*</span>
                </Form.Label>
                <Form.Control
                  as='textarea'
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder='Enter detailed reason for rejection...'
                  required
                />
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label>Manager Remarks (Optional)</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={2}
                  value={managerRemarks}
                  onChange={(e) => setManagerRemarks(e.target.value)}
                  placeholder='Additional comments...'
                />
              </Form.Group>

              <Alert variant='warning'>
                <small>
                  <strong>This will:</strong>
                  <ul className='mb-0 mt-2'>
                    <li>Set entry status to REJECTED</li>
                    <li>Notify employee via email</li>
                    <li>Allow employee to edit and resubmit</li>
                  </ul>
                </small>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button variant='danger' onClick={handleRejectEntry} disabled={submitting}>
            {submitting ? 'Rejecting...' : 'Confirm Rejection'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Team Members Modal */}
      <Modal show={showTeamModal} onHide={() => setShowTeamModal(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className='fas fa-users me-2'></i>
            Team Members ({teamMembers.length})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {teamMembers.length > 0 ? (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Employee Code</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {teamMembers.map((member, index) => (
                  <tr key={member.id}>
                    <td>{index + 1}</td>
                    <td>
                      <i className='fas fa-user me-2 text-primary'></i>
                      <strong>{member.name}</strong>
                    </td>
                    <td>
                      <Badge bg='secondary'>{member.employeeCode || 'N/A'}</Badge>
                    </td>
                    <td>
                      <Button
                        size='sm'
                        variant='outline-primary'
                        onClick={() => {
                          setSelectedEmployee(member.id)
                          setShowTeamModal(false)
                        }}
                      >
                        <i className='fas fa-filter me-1'></i>
                        View Timesheets
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className='text-center py-5'>
              <i className='fas fa-users fa-3x text-muted mb-3'></i>
              <p className='text-muted'>No team members found.</p>
              <small className='text-muted'>
                If you believe this is an error, please contact your administrator to ensure team members are assigned to you.
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowTeamModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ManagerApproval

